import { getCityFromLocale } from '@repo/utils'
import GoogleMapReact from 'google-map-react'
import React, { useEffect, useState } from 'react'
import Supercluster from 'supercluster'

import s from './styles.module.scss'

import { MapCardProps } from './components/map-card/map-card.types'
import { ClusterMarker, SimpleMarker } from './components/Markers'
import { defaultProps } from './map.constants'
import {
  CoordinateMap,
  HouseProperties,
  MapElement,
  MapPayload,
} from './map.types'
import { useTravelTypeContext } from '../../contexts'
import { Loader } from '../../molecules'
import { useLocale } from '../../../hooks/use-locale'
import { TravelTypeEnum } from '../../../lib/graphql'
import { breakpoints, useMediaQuery } from '../../../lib/hooks'
import { DEFAULT_MINIMUM_VALUE_IN_CENTS, roundedPrice } from '../../../utils'

export const Map = <
  Payload extends MapPayload = MapPayload,
  Element extends MapElement = MapElement,
>({
  CardElement,
  dataMatchs,
  onClickItem,
  style = {},
  styleContainerMap = {},
  scrollwheel = true,
  inHousePage = false,
  zoomOnLoad = false,
  mapHoveredHouse,
  hideZoomControls = false,
  setMapHoveredHouse,
  setMapGoToRef,
}: {
  CardElement: React.FunctionComponent<MapCardProps<Element>>
  dataMatchs: Payload[]
  onClickItem?: (arg0: string) => void
  style?: React.CSSProperties
  styleContainerMap?: React.CSSProperties
  scrollwheel?: boolean
  inHousePage?: boolean
  zoomOnLoad?: boolean
  mapHoveredHouse?: string
  hideZoomControls?: boolean
  setMapHoveredHouse?: React.Dispatch<React.SetStateAction<string | undefined>>
  setMapGoToRef?: React.Dispatch<React.SetStateAction<string | undefined>>
}) => {
  const isMobile = useMediaQuery(breakpoints.newMobile)
  const [selectedElement, setSelectedElement] = useState<Element | undefined>(
    undefined,
  )
  const { locale } = useLocale()
  const DEFAULT_CENTER: CoordinateMap = getCityFromLocale(locale)

  const [clusters, setClusters] = useState<
    (
      | Supercluster.PointFeature<HouseProperties>
      | Supercluster.ClusterFeature<Supercluster.ClusterProperties>
    )[]
  >([])
  const [isLoaded, setIsLoaded] = useState(false)
  const [mapsData, setMapsData] = useState<{
    map?: google.maps.Map
    maps?: google.maps.Map[]
  }>({})

  const { travelType } = useTravelTypeContext()

  const formatPointsArray = () => {
    const points: Supercluster.PointFeature<HouseProperties>[] = []

    for (const { house } of dataMatchs) {
      if (!points.some((x) => x.id === house._id)) {
        const longitude = house?.address?.longitude
        const latitude = house?.address?.latitude

        if (longitude && latitude) {
          const startingFees =
            typeof house.startingFees === 'number'
              ? house.startingFees
              : travelType === TravelTypeEnum.NIGHTLY_TRIP
                ? house.startingFees?.price
                : house.startingFees?.dailyPrice

          points.push({
            type: 'Feature',
            properties: {
              houseId: house._id,
              price: startingFees
                ? roundedPrice(startingFees, {
                    minimumValueInCents: DEFAULT_MINIMUM_VALUE_IN_CENTS,
                    currency: house.currencyArea.currency,
                  })
                : undefined,
              cluster: false,
            },
            geometry: {
              type: 'Point',
              coordinates: [longitude, latitude],
            },
          })
        }
      }
    }

    return points
  }

  const supercluster = new Supercluster({
    minZoom: defaultProps.options.minZoom,
    maxZoom: defaultProps.options.maxZoom,
    radius: defaultProps.clusterRadius,
    minPoints: 5,
  }).load(formatPointsArray())

  const mapOptions = () => {
    return {
      fullscreenControl: false,
      scrollwheel: scrollwheel,
      zoomControl: !hideZoomControls,
      zoomControlOptions: {
        position: google.maps.ControlPosition.TOP_LEFT,
      },
      // maxZoom: MAX_ZOOM,
      disableDoubleClickZoom: true,
      clickableIcons: false,
    }
  }

  const getZoom = () => {
    return mapsData.map?.getZoom() ?? defaultProps.options.zoom
  }

  const handleChange = () => {
    if (dataMatchs.length === 0) {
      return
    }

    getClusters()
  }

  const getClusters = () => {
    const bounds = mapsData.map?.getBounds()
    if (bounds) {
      const ne = bounds.getNorthEast()
      const sw = bounds.getSouthWest()
      setClusters(
        supercluster.getClusters(
          [sw.lng(), sw.lat(), ne.lng(), ne.lat()],
          getZoom(),
        ) as (
          | Supercluster.PointFeature<HouseProperties>
          | Supercluster.ClusterFeature<Supercluster.ClusterProperties>
        )[],
      )
    }
  }

  useEffect(() => {
    getClusters()
  }, [mapsData])

  useEffect(() => {
    setTimeout(() => setIsLoaded(true), 1500)
  }, [])

  if (!isLoaded) {
    return <Loader />
  }

  return (
    <div className={s.container} style={style}>
      <div
        id='naboo_mapContainer'
        className={s.containerMap}
        style={styleContainerMap}
      >
        <GoogleMapReact
          bootstrapURLKeys={{ key: process.env.NEXT_PUBLIC_GOOGLE_KEY || '' }}
          defaultCenter={DEFAULT_CENTER}
          defaultZoom={defaultProps.options.zoom}
          onChange={handleChange}
          onChildMouseEnter={(key) =>
            setMapHoveredHouse && setMapHoveredHouse(key)
          }
          onChildMouseLeave={() =>
            setMapHoveredHouse && setMapHoveredHouse(undefined)
          }
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({
            map,
            maps,
          }: {
            map: google.maps.Map
            maps: google.maps.Map[]
          }) => {
            setMapsData({ map, maps })
            if (!dataMatchs.length) {
              return map.setCenter(DEFAULT_CENTER)
            }

            if (zoomOnLoad) {
              const bounds = new window.google.maps.LatLngBounds()
              dataMatchs.map((data: Payload) => {
                bounds.extend({
                  lat: Number(data.house?.address?.latitude ?? ''),
                  lng: Number(data.house?.address?.longitude ?? ''),
                })
              })

              map.fitBounds(bounds)
            }
          }}
          options={mapOptions}
        >
          {clusters.length > 0 &&
            clusters.map(
              (
                cluster:
                  | Supercluster.PointFeature<HouseProperties>
                  | Supercluster.ClusterFeature<Supercluster.ClusterProperties>,
              ) => {
                if (cluster.properties.cluster === false) {
                  const point =
                    cluster as Supercluster.PointFeature<HouseProperties>
                  return (
                    <SimpleMarker
                      key={`${point.properties.houseId}`}
                      lat={point.geometry.coordinates[1]}
                      lng={point.geometry.coordinates[0]}
                      text={point.properties.price || '-'}
                      isSelected={
                        selectedElement?.house._id ===
                          point.properties.houseId ||
                        cluster.properties.houseId === mapHoveredHouse
                      }
                      onMouseEnter={() =>
                        setMapHoveredHouse &&
                        setMapHoveredHouse(point.properties.houseId)
                      }
                      onMouseLeave={() =>
                        setMapHoveredHouse && setMapHoveredHouse(undefined)
                      }
                      onClick={() => {
                        if (inHousePage) {
                          return
                        }

                        setMapGoToRef && setMapGoToRef(point.properties.houseId)

                        const matchElement = dataMatchs
                          .filter(
                            (x) => x.house._id === point.properties.houseId,
                          )
                          .at(0) as Omit<Element, 'lat' | 'lng'> | undefined

                        if (matchElement) {
                          setSelectedElement({
                            ...matchElement,
                            lat: point.geometry.coordinates[1],
                            lng: point.geometry.coordinates[0],
                          } as Element)
                        }
                      }}
                      onDoubleClick={() => {
                        mapsData.map?.setCenter({
                          lat: point.geometry.coordinates[1],
                          lng: point.geometry.coordinates[0],
                        })
                        mapsData.map?.setZoom(getZoom() + 3)
                      }}
                      zoom={getZoom()}
                      bounds={mapsData.map?.getBounds()}
                      center={mapsData.map?.getCenter()}
                    />
                  )
                } else {
                  cluster =
                    cluster as Supercluster.ClusterFeature<Supercluster.ClusterProperties>
                  let isSelected = false
                  if (mapHoveredHouse) {
                    supercluster
                      .getChildren(cluster.properties.cluster_id)
                      .map((child) => {
                        if (child.properties.houseId === mapHoveredHouse) {
                          isSelected = true
                        }
                      })
                  }

                  return (
                    <ClusterMarker
                      key={cluster.properties.cluster_id}
                      lat={cluster.geometry.coordinates[1]}
                      lng={cluster.geometry.coordinates[0]}
                      isSelected={isSelected}
                      text={String(cluster.properties.point_count)}
                      onClick={() => {
                        const zoomOnCluster =
                          supercluster.getClusterExpansionZoom(
                            (
                              cluster.properties as Supercluster.ClusterProperties
                            ).cluster_id,
                          )
                        mapsData.map?.setCenter({
                          lat: cluster.geometry.coordinates[1],
                          lng: cluster.geometry.coordinates[0],
                        })
                        mapsData.map?.setZoom(zoomOnCluster)
                      }}
                      zoom={getZoom()}
                      bounds={mapsData.map?.getBounds()}
                      center={mapsData.map?.getCenter()}
                    />
                  )
                }
              },
            )}
          {selectedElement && !isMobile ? (
            <CardElement
              element={selectedElement}
              lat={selectedElement.lat}
              lng={selectedElement.lng}
              onClick={() => {
                if (onClickItem) {
                  onClickItem(selectedElement.house._id)
                }
              }}
              onClose={() => setSelectedElement(undefined)}
              className={s.cardElement}
            />
          ) : null}
        </GoogleMapReact>

        {selectedElement && isMobile ? (
          <CardElement
            element={selectedElement}
            lat={selectedElement.lat}
            lng={selectedElement.lng}
            onClick={() => {
              if (onClickItem) {
                onClickItem(selectedElement.house._id)
              }
            }}
            onClose={() => setSelectedElement(undefined)}
            className={s.fixedCardElement}
          />
        ) : null}
      </div>
    </div>
  )
}
